import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { useFileConflictModalStore } from "../store/zustand/FileConflictModal";
import { X, Loader2, FileWarning } from "lucide-react";
import { memo } from "react";

interface FileConflictModalProps {
  fileName: string;
  filePath: string;
  onReplace: () => Promise<void>;
  onCancel: () => void;
  isOpen: boolean;
  isProcessing: boolean;
}

export const FileConflictModal = ({
  fileName,
  filePath,
  onReplace,
  onCancel,
  isOpen,
  isProcessing,
}: FileConflictModalProps) => {
  const handleReplace = async () => {
    await onReplace();
  };

  const hideModal = useFileConflictModalStore((state) => state.hideModal);

  const handleCancel = () => {
    hideModal();
    onCancel();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isProcessing) {
      handleCancel();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="bg-[#1C1C1C] border-0 text-white gap-4 p-4">
        <AlertDialogHeader className="relative">
          <AlertDialogTitle className="text-sm flex items-center gap-2">
            <FileWarning size={16} className="text-primary-0" />
            File already exists
          </AlertDialogTitle>
          <button
            type="button"
            onClick={handleCancel}
            className="text-text-secondary hover:text-text-primary absolute -right-0 -top-2"
            disabled={isProcessing}
          >
            <X size={16} />
          </button>

          <AlertDialogDescription className="text-xs">
            A file with this name already exists in your codebase. Do you want
            to replace it?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="mb-4">
          <div className="text-gray-400 text-xs mb-2">File to replace</div>
          <div className="bg-background-secondary rounded p-2 text-text-primary font-mono text-xs">
            <div className="font-medium">{fileName}</div>
            <div className="text-gray-400 text-2xs">{filePath}</div>
          </div>
        </div>

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            className="bg-background-secondary text-text-primary border-0 hover:bg-background-secondary/80 text-xs"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleReplace}
            className="bg-primary-0 text-text-primary hover:bg-background-primary/80 text-xs"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                Replacing...
              </span>
            ) : (
              "Replace File"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const FileConflictModalWrapper = memo(() => {
  const isOpen = useFileConflictModalStore((state) => state.isOpen);
  const fileName = useFileConflictModalStore((state) => state.fileName);
  const filePath = useFileConflictModalStore((state) => state.filePath);
  const onReplace = useFileConflictModalStore((state) => state.onReplace);
  const onCancel = useFileConflictModalStore((state) => state.onCancel);
  const isProcessing = useFileConflictModalStore((state) => state.isProcessing);

  if (!onReplace) return null;
  return (
    <FileConflictModal
      fileName={fileName}
      filePath={filePath}
      onReplace={onReplace}
      onCancel={onCancel}
      isOpen={isOpen}
      isProcessing={isProcessing}
    />
  );
});
